import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAlert } from "../context/AlertMessageContext";
import { useAuth } from "../context/AuthContext";
import Alerts from "./Alert";
import { formatConvocationDate } from "../utils/dateFormat";

const RegistrationForm = ({ singleUser, setSingleUser }) => {
  const { token } = useAuth();
  const [attending, setAttending] = useState("inPerson");
  const [companions, setCompanions] = useState("0");
  const [date, setDate] = useState("01-11-2026");
  const [selectTerms, setSelectTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [alertVariant, setAlertVariant] = useState("success");
  const [isRegistrationOpen, setIsRegistrationOpen] = useState(true);
  const { setMessage } = useAlert();

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE}/auth/registration-status`
        );
        if (res.data && res.data.success) {
          setIsRegistrationOpen(res.data.is_open);
        }
      } catch (err) {
        console.error("Failed to fetch registration status:", err);
      }
    };
    fetchStatus();
  }, []);

  /* ---------- helpers ---------- */
  const isFormValid = () =>
    isRegistrationOpen &&
    (attending === "courier"
      ? selectTerms
      : attending === "inPerson" && companions !== "" && date !== "" && date !== "31-10-2026" && selectTerms);

  /* ---------- handlers ---------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setLoading(true);

    try {
      const payload = {
        reg_no: singleUser.reg_no,
        companions: attending === "inPerson" ? companions : "0",
        attending,
        date: attending === "inPerson" ? date : "Courier",
      };

      const { data } = await axios.put(
        `${process.env.REACT_APP_API_BASE}/student/update-registration-status`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (data.message === "Student registration successful") {
        setAlertVariant("success");
        setMessage(
          attending === "inPerson"
            ? "Convocation registration confirmed! Please check your email."
            : "Your courier registration has been confirmed!"
        );
        setSingleUser((prev) => ({
          ...prev,
          companions: payload.companions,
          day: payload.date,
          date: payload.date,
          attending: payload.attending,
        }));
      } else {
        setAlertVariant("danger");
        setMessage("Registration Failed");
      }
    } catch (error) {
      setAlertVariant("danger");
      const errMsg = error?.response?.data?.message || "Registration Failed";
      setMessage(errMsg);
    } finally {
      setOpen(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setOpen(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const isRegistered = singleUser.day || singleUser.date;

  return (
    <div
      style={{
        background: "#ffffff",
        padding: "28px",
        borderRadius: "14px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        width: "100%",
        maxWidth: "600px",
        marginLeft: "15px",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      {open && <Alerts variant={alertVariant} />}

      {!isRegistered ? (
        <form onSubmit={handleSubmit}>
          <h3
            style={{
              marginBottom: "16px",
              fontWeight: 700,
              textAlign: "center",
              color: "#1a202c",
              borderBottom: "2px solid #f0f2f5",
              paddingBottom: "12px",
            }}
          >
            Convocation Attendance
          </h3>

          {!isRegistrationOpen && (
            <div
              style={{
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "12px 14px",
                fontSize: "13px",
                color: "#991b1b",
                fontWeight: 600,
                marginBottom: "18px",
                textAlign: "center",
              }}
            >
              Convocation registrations have closed by the administration.
            </div>
          )}

          <div style={{ marginBottom: "20px" }}>
            <label
              style={{
                marginBottom: "10px",
                fontWeight: 600,
                display: "block",
                color: "#2d3748",
              }}
            >
              How will you collect your Degree Certificate?
            </label>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                padding: "10px 0",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: attending === "inPerson" ? "2px solid #e06d15" : "1px solid #e2e8f0",
                  backgroundColor: attending === "inPerson" ? "#fffaf5" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  type="radio"
                  name="attend"
                  value="inPerson"
                  checked={attending === "inPerson"}
                  onChange={(e) => setAttending(e.target.value)}
                  style={{ accentColor: "#e06d15", width: "18px", height: "18px" }}
                />
                <span style={{ fontWeight: 500, color: "#1a202c" }}>
                  Attend Convocation & Collect Degree In Person
                </span>
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: attending === "courier" ? "2px solid #e06d15" : "1px solid #e2e8f0",
                  backgroundColor: attending === "courier" ? "#fffaf5" : "#fff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <input
                  type="radio"
                  name="attend"
                  value="courier"
                  checked={attending === "courier"}
                  onChange={(e) => setAttending(e.target.value)}
                  style={{ accentColor: "#e06d15", width: "18px", height: "18px" }}
                />
                <span style={{ fontWeight: 500, color: "#1a202c" }}>
                  Receive Degree Certificate via Courier Service
                </span>
              </label>
            </div>
          </div>

          {attending === "inPerson" && (
            <div
              style={{
                backgroundColor: "#f8fafc",
                padding: "16px",
                borderRadius: "10px",
                marginBottom: "20px",
                border: "1px solid #edf2f7",
              }}
            >
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    marginBottom: "8px",
                    fontWeight: 600,
                    display: "block",
                    color: "#2d3748",
                  }}
                >
                  Number of Companions / Guests attending with you:
                </label>
                <select
                  value={companions}
                  onChange={(e) => setCompanions(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "15px",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="0">0 (Self Only)</option>
                  <option value="1">1 Companion</option>
                  <option value="2">2 Companions (Max)</option>
                </select>
              </div>

              <div>
                <label
                  style={{
                    marginBottom: "8px",
                    fontWeight: 600,
                    display: "block",
                    color: "#2d3748",
                  }}
                >
                  Convocation Date:
                </label>
                <select
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "15px",
                    backgroundColor: "#fff",
                  }}
                >
                  <option value="01-11-2026">1st November 2026 (Sunday)</option>
                </select>
              </div>
            </div>
          )}

          <div style={{ marginBottom: "22px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
                cursor: "pointer",
                fontSize: "14px",
                color: "#4a5568",
                lineHeight: "1.4",
              }}
            >
              <input
                type="checkbox"
                checked={selectTerms}
                onChange={(e) => setSelectTerms(e.target.checked)}
                style={{
                  accentColor: "#e06d15",
                  width: "18px",
                  height: "18px",
                  marginTop: "2px",
                }}
              />
              <span>
                I hereby declare that the information provided above is accurate and true to the best of my knowledge.
              </span>
            </label>
          </div>

          <div style={{ textAlign: "center" }}>
            <button
              type="submit"
              disabled={!isFormValid() || loading}
              style={{
                width: "100%",
                padding: "12px 24px",
                backgroundColor: !isFormValid() || loading ? "#cbd5e1" : "#e06d15",
                color: "#fff",
                fontWeight: 600,
                fontSize: "16px",
                border: "none",
                borderRadius: "8px",
                cursor: !isFormValid() || loading ? "not-allowed" : "pointer",
                boxShadow: !isFormValid() || loading ? "none" : "0 4px 12px rgba(224, 109, 21, 0.3)",
                transition: "all 0.2s ease",
              }}
            >
              {loading ? "Submitting..." : "Confirm Convocation Registration"}
            </button>
          </div>
        </form>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 10px" }}>
          <h4 style={{ fontWeight: 700, color: "#198754", marginBottom: "10px" }}>
            Registration Complete
          </h4>
          <p style={{ color: "#4a5568", fontSize: "15px" }}>
            You have successfully registered for the Convocation.
          </p>
          <div
            style={{
              backgroundColor: "#f8fafc",
              padding: "16px",
              borderRadius: "10px",
              marginTop: "16px",
              textAlign: "left",
              border: "1px solid #edf2f7",
            }}
          >
            <p style={{ margin: "4px 0", color: "#2d3748" }}>
              <strong>Mode:</strong> {singleUser.attending === "inPerson" ? "In Person" : "Courier Delivery"}
            </p>
            {singleUser.attending === "inPerson" && (
              <>
                <p style={{ margin: "4px 0", color: "#2d3748" }}>
                  <strong>Selected Date:</strong> {formatConvocationDate(singleUser.day || singleUser.date)}
                </p>
                <p style={{ margin: "4px 0", color: "#2d3748" }}>
                  <strong>Companions:</strong> {singleUser.companions || "0"}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegistrationForm;
